import { inject, Injectable, signal } from '@angular/core';
import { getDownloadURL, ref, Storage } from '@angular/fire/storage';
import {
  DocumentReference,
  httpsCallable,
  serialise,
  SerialisedData,
  snapshot,
  snapshotDefined,
  unserialise,
} from '@principle-theorem/shared';
import { versionedSchemaToHtml } from '@principle-theorem/editor';
import {
  IDocumentSettings,
  IPatient,
  IPDFContent,
  IPDFContentRequest,
  IPDFContentResponse,
  ISavePdfToPatientFileRequest,
} from '@principle-theorem/principle-core/interfaces';
import { OrganisationService } from '../organisation.service';
import * as moment from 'moment-timezone';
import { MatSnackBar } from '@angular/material/snack-bar';
import { type AnyExtension } from '@tiptap/core';

@Injectable({
  providedIn: 'root',
})
export class PdfService {
  private _organisation = inject(OrganisationService);
  private _snack = inject(MatSnackBar);
  private _storage = inject(Storage);

  generatingPdf = signal(false);

  /**
   * Generates a PDF and triggers the browser print dialog.
   * Attempts a hidden iframe for a seamless experience, falling back
   * to `window.open` when cross-origin restrictions block iframe
   * `contentWindow` access (e.g. Firefox with blob URLs).
   */
  async print(content: IPDFContent, extensions: AnyExtension[]): Promise<void> {
    const blob = await this.generateBlob(content, extensions);
    this.printBlob(blob);
  }

  /**
   * Generates a PDF and triggers a file download with the given filename.
   * Falls back to a timestamped filename if none is provided.
   */
  async download(
    content: IPDFContent,
    extensions: AnyExtension[],
    fileName?: string
  ): Promise<void> {
    const blob = await this.generateBlob(content, extensions);
    this.downloadBlob(blob, fileName);
  }

  /**
   * Calls the server-side `generatePdf` Cloud Function, returning the
   * resulting PDF as a `Blob`.
   */
  async generateBlob(
    content: IPDFContent,
    extensions: AnyExtension[]
  ): Promise<Blob> {
    const { bodyHtml, headerHtml, footerHtml } = this._renderHtml(
      content,
      extensions
    );

    return this.generateBlobFromHtml(
      bodyHtml,
      headerHtml,
      footerHtml,
      content.documentSettings
    );
  }

  async saveToPatientFiles(
    patientRef: DocumentReference<IPatient>,
    content: IPDFContent,
    extensions: AnyExtension[],
    fileName?: string
  ): Promise<void> {
    const organisation = await snapshotDefined(
      this._organisation.organisation$
    );

    const request: ISavePdfToPatientFileRequest = {
      organisationRef: organisation.ref,
      ...this._renderHtml(content, extensions),
      documentSettings: content.documentSettings,
      patientRef,
      fileName,
    };

    await this._generatePdf(request);
  }

  /**
   * Calls the server-side `generatePdf` Cloud Function with pre-built
   * HTML strings, returning the resulting PDF as a `Blob`.
   */
  async generateBlobFromHtml(
    bodyHtml: string,
    headerHtml?: string,
    footerHtml?: string,
    documentSettings?: IDocumentSettings
  ): Promise<Blob> {
    const organisation = await snapshotDefined(
      this._organisation.organisation$
    );

    const { storageUrl } = await this._generatePdf({
      organisationRef: organisation.ref,
      bodyHtml,
      headerHtml,
      footerHtml,
      documentSettings,
    });

    const downloadUrl = await getDownloadURL(ref(this._storage, storageUrl));
    const download = await fetch(downloadUrl);
    return download.blob();
  }

  /**
   * Triggers the browser print dialog for a PDF blob.
   * Attempts a hidden iframe for a seamless experience, falling back
   * to `window.open` when cross-origin restrictions block iframe
   * `contentWindow` access.
   */
  printBlob(blob: Blob): void {
    const blobUrl = URL.createObjectURL(blob);
    this._printViaIframe(blobUrl);
  }

  /**
   * Triggers a file download for a PDF blob.
   * Falls back to a timestamped filename if none is provided.
   */
  downloadBlob(blob: Blob, fileName?: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    const timestamp = moment().format('Do-MMM-YYYY');
    const defaultFileName = fileName
      ? `${fileName}.pdf`
      : `document_${timestamp}.pdf`;

    link.href = url;
    link.download = defaultFileName;
    link.click();
    window.URL.revokeObjectURL(url);
    this._snack.open('Download started');
  }

  private async _generatePdf(
    request: IPDFContentRequest
  ): Promise<IPDFContentResponse> {
    const generatePDF = httpsCallable<
      SerialisedData<IPDFContentRequest>,
      SerialisedData<IPDFContentResponse>
    >('http-pdf-generatePdf');

    this.generatingPdf.set(true);

    try {
      const response = await snapshot(generatePDF(serialise(request)));
      return unserialise<IPDFContentResponse>(response);
    } finally {
      this.generatingPdf.set(false);
    }
  }

  private _renderHtml(
    content: IPDFContent,
    extensions: AnyExtension[]
  ): Pick<IPDFContentRequest, 'bodyHtml' | 'headerHtml' | 'footerHtml'> {
    const { body, header, footer } = content;

    return {
      bodyHtml: versionedSchemaToHtml(body, extensions),
      headerHtml: header
        ? versionedSchemaToHtml(header, extensions)
        : undefined,
      footerHtml: footer
        ? versionedSchemaToHtml(footer, extensions)
        : undefined,
    };
  }

  /**
   * Prints a PDF by embedding it in a hidden iframe and calling `print()`
   * on the iframe's `contentWindow`. Provides a seamless in-page print
   * experience. If the browser treats the blob URL as cross-origin
   * (e.g. Firefox), the resulting `SecurityError` is caught internally
   * and {@link _printViaWindow} is called automatically as a fallback.
   */
  private _printViaIframe(blobUrl: string): void {
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = blobUrl;
    document.body.appendChild(iframe);

    iframe.onerror = () => {
      iframe.remove();
      this._printViaWindow(blobUrl);
    };

    iframe.onload = () => {
      try {
        const contentWindow = iframe.contentWindow;
        if (!contentWindow) {
          throw new Error('No contentWindow');
        }

        if (!contentWindow.document) {
          throw new Error('No document');
        }

        const cleanup = (): void => {
          URL.revokeObjectURL(blobUrl);
          iframe.remove();
        };

        contentWindow.addEventListener('beforeunload', cleanup);
        contentWindow.addEventListener('afterprint', cleanup);
        contentWindow.print();
      } catch {
        iframe.remove();
        this._printViaWindow(blobUrl);
      }
    };
  }

  /**
   * Prints a PDF by opening it in a new browser tab via `window.open`.
   * Used as a fallback when iframe-based printing is blocked by
   * cross-origin restrictions. The tab is automatically closed after
   * the print dialog is dismissed. The blob URL is also revoked on
   * `beforeunload` to prevent leaks if the tab is closed without printing.
   */
  private _printViaWindow(blobUrl: string): void {
    const printWindow = window.open(blobUrl);
    if (!printWindow) {
      URL.revokeObjectURL(blobUrl);
      this._snack.open('Failed to open print window. Please allow pop-ups.');
      return;
    }

    const cleanup = (): void => {
      URL.revokeObjectURL(blobUrl);
      printWindow.close();
    };

    printWindow.onload = () => {
      printWindow.addEventListener('beforeunload', cleanup);
      printWindow.addEventListener('afterprint', cleanup);
      printWindow.print();
    };
  }
}
